import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, ExternalLink, Pause, Play, RefreshCw, Trash2, Eye, Clock, X, Loader2, Globe, AlertCircle, History, Upload, Link2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VersionHistory from './VersionHistory';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  live: { label: 'En vivo', color: 'bg-green-500', textColor: 'text-green-400', icon: Globe },
  paused: { label: 'Pausado', color: 'bg-yellow-500', textColor: 'text-yellow-400', icon: Pause },
  error: { label: 'Error', color: 'bg-red-500', textColor: 'text-red-400', icon: AlertCircle }
};

export function DeployedApps({ onDeploy }) {
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistoryDeployment, setVersionHistoryDeployment] = useState(null);
  const [vercelStatus, setVercelStatus] = useState({ connected: false });
  const [showVercelConnect, setShowVercelConnect] = useState(false);
  const [vercelToken, setVercelToken] = useState('');
  const [connectingVercel, setConnectingVercel] = useState(false);
  const [deployingToVercel, setDeployingToVercel] = useState(null);
  // Legacy state for old versions modal (kept for backward compatibility)
  const [showVersions, setShowVersions] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [versions, setVersions] = useState([]);

  const openVersionHistory = (deployment) => {
    setVersionHistoryDeployment(deployment);
    setShowVersionHistory(true);
  };

  const fetchVercelStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/vercel/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVercelStatus(response.data);
    } catch (e) {
      console.log('Vercel status check failed');
    }
  }, []);

  const fetchDeployments = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/deployments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeployments(response.data.deployments || []);
    } catch (e) {
      console.error('Error fetching deployments:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeployments();
    fetchVercelStatus();
  }, [fetchDeployments, fetchVercelStatus]);

  const connectVercel = async () => {
    if (!vercelToken.trim()) {
      toast.error('Ingresa tu token de Vercel');
      return;
    }
    setConnectingVercel(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/vercel/connect`, 
        { vercel_token: vercelToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setVercelStatus({ connected: true, username: response.data.vercel_username });
      setShowVercelConnect(false);
      setVercelToken('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al conectar');
    } finally {
      setConnectingVercel(false);
    }
  };

  const deployToVercel = async (deployment) => {
    setDeployingToVercel(deployment.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/vercel/deploy`,
        { deployment_id: deployment.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`¡Desplegado en Vercel! ${response.data.vercel_url}`);
      fetchDeployments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al desplegar en Vercel');
    } finally {
      setDeployingToVercel(null);
    }
  };

  const updateStatus = async (deploymentId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/deployments/${deploymentId}/status`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Estado actualizado a ${STATUS_CONFIG[status].label}`);
      fetchDeployments();
    } catch (e) {
      toast.error('Error al actualizar estado');
    }
  };

  const redeploy = async (deploymentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/deployments/${deploymentId}/redeploy`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchDeployments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al redesplegar');
    }
  };

  const deleteDeployment = async (deploymentId) => {
    if (!window.confirm('¿Eliminar este despliegue? Esta acción no se puede deshacer.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/deployments/${deploymentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Despliegue eliminado');
      fetchDeployments();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const fetchVersions = async (deployment) => {
    setSelectedDeployment(deployment);
    setShowVersions(true);
    setLoadingVersions(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/deployments/${deployment.id}/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersions(response.data.versions || []);
    } catch (e) {
      toast.error('Error al cargar versiones');
    } finally {
      setLoadingVersions(false);
    }
  };

  const rollback = async (versionId) => {
    if (!selectedDeployment) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/deployments/${selectedDeployment.id}/rollback/${versionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowVersions(false);
      fetchDeployments();
    } catch (e) {
      toast.error('Error al hacer rollback');
    }
  };

  const openPreview = (deployment) => {
    const previewUrl = `${API}${deployment.preview_url}`;
    window.open(previewUrl, '_blank');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B00]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="deployed-apps-section">
      {/* Vercel Connection Banner */}
      <div className="glass rounded-xl p-4 border border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${vercelStatus.connected ? 'bg-green-500/20' : 'bg-white/5'}`}>
              <Upload className={`w-5 h-5 ${vercelStatus.connected ? 'text-green-400' : 'text-gray-500'}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-white">Vercel</p>
              <p className="text-xs text-gray-500">
                {vercelStatus.connected ? `Conectado como ${vercelStatus.username}` : 'Conecta para dominios personalizados'}
              </p>
            </div>
          </div>
          {vercelStatus.connected ? (
            <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs">Conectado</span>
          ) : (
            <button 
              onClick={() => setShowVercelConnect(true)}
              className="px-3 py-1.5 rounded-lg bg-[#FF6B00]/20 text-[#FF6B00] text-xs font-medium hover:bg-[#FF6B00]/30"
            >
              Conectar Vercel
            </button>
          )}
        </div>
      </div>

      {deployments.length === 0 ? (
        <div className="text-center py-12 glass rounded-xl border border-white/10">
          <Rocket className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <h3 className="text-lg font-medium text-white mb-2">Sin despliegues</h3>
          <p className="text-gray-500 text-sm mb-4">Genera código en un proyecto y despliégalo aquí</p>
          {onDeploy && (
            <button onClick={onDeploy} className="px-4 py-2 rounded-lg bg-[#FF6B00] text-white text-sm font-medium hover:bg-[#FF8533]">
              Crear primer despliegue
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {deployments.map((deployment) => {
            const statusConfig = STATUS_CONFIG[deployment.status] || STATUS_CONFIG.live;
            const StatusIcon = statusConfig.icon;
            
            return (
              <motion.div
                key={deployment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass rounded-xl border border-white/10 overflow-hidden hover:border-white/20 transition-colors"
                data-testid={`deployment-card-${deployment.id}`}
              >
                {/* Header */}
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-white truncate flex-1 mr-2">{deployment.name}</h3>
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusConfig.color}/20 ${statusConfig.textColor}`}>
                      <StatusIcon className="w-3 h-3" />
                      {statusConfig.label}
                    </span>
                  </div>
                  {deployment.description && (
                    <p className="text-xs text-gray-500 line-clamp-2">{deployment.description}</p>
                  )}
                  {deployment.vercel_url && (
                    <a href={deployment.vercel_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-400 mt-2 hover:underline">
                      <Link2 className="w-3 h-3" />
                      {deployment.vercel_url.replace('https://', '')}
                    </a>
                  )}
                </div>
                
                {/* Stats */}
                <div className="px-4 py-3 flex items-center gap-4 text-xs text-gray-500 border-b border-white/10">
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {deployment.view_count} vistas
                  </span>
                  <span className="flex items-center gap-1">
                    <History className="w-3 h-3" />
                    v{deployment.version_count}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(deployment.updated_at)}
                  </span>
                </div>
                
                {/* Actions */}
                <div className="p-3 flex items-center gap-2">
                  <button
                    onClick={() => openPreview(deployment)}
                    disabled={deployment.status === 'paused'}
                    className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg bg-[#FF6B00]/20 text-[#FF6B00] text-sm font-medium hover:bg-[#FF6B00]/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`visit-btn-${deployment.id}`}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Visitar
                  </button>
                  
                  {/* Vercel deploy button */}
                  {vercelStatus.connected && !deployment.vercel_url && (
                    <button
                      onClick={() => deployToVercel(deployment)}
                      disabled={deployingToVercel === deployment.id}
                      className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400 text-sm font-medium hover:bg-blue-500/30 disabled:opacity-50"
                      title="Desplegar en Vercel"
                    >
                      {deployingToVercel === deployment.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  
                  <div className="flex items-center gap-1">
                    {deployment.status === 'live' ? (
                      <button
                        onClick={() => updateStatus(deployment.id, 'paused')}
                        className="p-2 rounded-lg hover:bg-yellow-500/20 text-yellow-400"
                        title="Pausar"
                      >
                        <Pause className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => updateStatus(deployment.id, 'live')}
                        className="p-2 rounded-lg hover:bg-green-500/20 text-green-400"
                        title="Reactivar"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => redeploy(deployment.id)}
                      className="p-2 rounded-lg hover:bg-blue-500/20 text-blue-400"
                      title="Redesplegar"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => openVersionHistory(deployment)}
                      className="p-2 rounded-lg hover:bg-purple-500/20 text-purple-400"
                      title="Historial de versiones"
                      data-testid={`version-history-btn-${deployment.id}`}
                    >
                      <History className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => deleteDeployment(deployment.id)}
                      className="p-2 rounded-lg hover:bg-red-500/20 text-red-400"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      
      {/* Versions Modal */}
      <AnimatePresence>
        {showVersions && selectedDeployment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowVersions(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-md glass rounded-2xl overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div>
                  <h3 className="font-semibold text-white">Historial de Versiones</h3>
                  <p className="text-xs text-gray-500">{selectedDeployment.name}</p>
                </div>
                <button onClick={() => setShowVersions(false)} className="p-2 rounded-lg hover:bg-white/10 text-gray-400">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 max-h-80 overflow-y-auto">
                {loadingVersions ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#FF6B00]" />
                  </div>
                ) : versions.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Sin versiones</p>
                ) : (
                  <div className="space-y-2">
                    {versions.map((version) => (
                      <div
                        key={version.id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          version.id === selectedDeployment.current_version_id
                            ? 'bg-[#FF6B00]/20 border border-[#FF6B00]/30'
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                      >
                        <div>
                          <span className="text-white font-medium">Versión {version.version_number}</span>
                          <p className="text-xs text-gray-500">{formatDate(version.created_at)}</p>
                        </div>
                        {version.id === selectedDeployment.current_version_id ? (
                          <span className="text-xs text-[#FF6B00] font-medium">Actual</span>
                        ) : (
                          <button
                            onClick={() => rollback(version.id)}
                            className="px-3 py-1 rounded-lg bg-white/10 text-white text-xs hover:bg-white/20"
                          >
                            Restaurar
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Vercel Connect Modal */}
      <AnimatePresence>
        {showVercelConnect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowVercelConnect(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-md glass rounded-2xl overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <Upload className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-white">Conectar Vercel</h3>
                </div>
                <button onClick={() => setShowVercelConnect(false)} className="p-2 rounded-lg hover:bg-white/10 text-gray-400">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-5 space-y-4">
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm">
                  <p className="text-blue-300 font-medium mb-1">¿Cómo obtener tu token?</p>
                  <ol className="text-blue-400/70 text-xs space-y-1">
                    <li>1. Ve a <a href="https://vercel.com/account/tokens" target="_blank" rel="noopener noreferrer" className="underline">vercel.com/account/tokens</a></li>
                    <li>2. Crea un nuevo token con scope &quot;Full Account&quot;</li>
                    <li>3. Copia y pega el token aquí</li>
                  </ol>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Token de Vercel</label>
                  <input
                    type="password"
                    value={vercelToken}
                    onChange={e => setVercelToken(e.target.value)}
                    placeholder="Pega tu token aquí..."
                    className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 p-4 border-t border-white/10">
                <button onClick={() => setShowVercelConnect(false)} className="px-4 py-2 rounded-lg text-gray-400 hover:text-white">
                  Cancelar
                </button>
                <button
                  onClick={connectVercel}
                  disabled={connectingVercel || !vercelToken.trim()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 disabled:opacity-50"
                >
                  {connectingVercel && <Loader2 className="w-4 h-4 animate-spin" />}
                  Conectar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Version History Modal */}
      {showVersionHistory && versionHistoryDeployment && (
        <VersionHistory
          deploymentId={versionHistoryDeployment.id}
          deploymentName={versionHistoryDeployment.name}
          currentVersionId={versionHistoryDeployment.current_version_id}
          onRollback={() => {
            fetchDeployments();
            toast.success('Versión restaurada exitosamente');
          }}
          onClose={() => {
            setShowVersionHistory(false);
            setVersionHistoryDeployment(null);
          }}
        />
      )}
    </div>
  );
}

export default DeployedApps;
